import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ServerCog, Building2, Users, FileSearch, ShieldAlert, Loader2,
  Search, UserX, UserCheck, Eraser, Crown, CreditCard, Ban,
  PlusCircle, Globe, Mail, Save, AlertTriangle,
} from 'lucide-react'
import {
  adminApi, type AdminTenant, type AdminUser, type AdminSubscription,
  type SubscriptionTier, type SubscriptionStatus,
} from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Callout } from '@/components/ui/callout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { roleLabel } from '@/lib/utils'

const PLAN_LABEL: Record<string, string> = { FREE: 'Free', SCHOOL: 'Scuola', NETWORK: 'Rete' }

function PlanBadge({ plan }: { plan: string }) {
  if (plan === 'FREE') {
    return <Badge variant="outline" className="text-xs text-muted-foreground">Free</Badge>
  }
  return (
    <Badge className="text-xs bg-primary/10 text-primary border-primary/30" variant="outline">
      {PLAN_LABEL[plan] || plan}
    </Badge>
  )
}

const SUB_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Attivo', EXPIRED: 'Scaduto', SUSPENDED: 'Sospeso', CANCELLED: 'Annullato',
}

/** Subscription history + manual activation for a single school. */
function TenantSubscriptions({ schoolId }: { schoolId: string }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const today = new Date().toISOString().slice(0, 10)
  const nextYear = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10)
  const [tier, setTier] = useState<SubscriptionTier>('SCHOOL')
  const [validFrom, setValidFrom] = useState(today)
  const [validUntil, setValidUntil] = useState(nextYear)
  const [orderRef, setOrderRef] = useState('')
  const [amount, setAmount] = useState('')
  const [quota, setQuota] = useState('')

  // Listino di riferimento (aggiornabile senza toccare il backend, che accetta
  // qualunque combinazione importo/quota): scelto un preset, precompila i
  // campi sottostanti, che restano comunque modificabili per l'offerta 4
  // "su preventivo dedicato".
  const PRESETS: { key: string; label: string; amount: string; quota: string }[] = [
    { key: 'custom', label: 'Personalizzato', amount: '', quota: '' },
    { key: 'base', label: '€99/anno · 50 valutazioni', amount: '99', quota: '50' },
    { key: 'plus', label: '€199/anno · 150 valutazioni', amount: '199', quota: '150' },
    { key: 'bespoke', label: 'Su preventivo · quota su misura', amount: '', quota: '' },
  ]
  const [preset, setPreset] = useState('custom')

  const { data: subs, isLoading } = useQuery({
    queryKey: ['admin-subscriptions', schoolId],
    queryFn: () => adminApi.listSubscriptions(schoolId),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-subscriptions', schoolId] })
    queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
  }

  const { mutate: create, isPending: creating } = useMutation({
    mutationFn: () =>
      adminApi.createSubscription(schoolId, {
        tier,
        valid_from: new Date(validFrom).toISOString(),
        valid_until: new Date(validUntil + 'T23:59:59').toISOString(),
        order_ref: orderRef.trim() || null,
        amount: amount ? Number(amount) : null,
        quota: quota ? Number(quota) : null,
      }),
    onSuccess: () => {
      refresh()
      setShowForm(false)
      setOrderRef('')
      setAmount('')
      setQuota('')
      setPreset('custom')
      toast({ title: 'Abbonamento attivato' })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  const { mutate: changeStatus } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SubscriptionStatus }) =>
      adminApi.setSubscriptionStatus(id, status),
    onSuccess: () => { refresh(); toast({ title: 'Stato abbonamento aggiornato' }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: changeQuota } = useMutation({
    mutationFn: ({ id, quota }: { id: string; quota: number | null }) =>
      adminApi.setSubscriptionQuota(id, quota),
    onSuccess: () => { refresh(); toast({ title: 'Quota aggiornata (upgrade/downgrade immediato)' }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const editQuota = (sub: AdminSubscription) => {
    const input = window.prompt(
      'Nuova quota di valutazioni incluse per l\'anno in corso (vuoto = illimitata):',
      sub.quota != null ? String(sub.quota) : '',
    )
    if (input === null) return
    const trimmed = input.trim()
    changeQuota({ id: sub.id, quota: trimmed === '' ? null : Number(trimmed) })
  }

  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <CreditCard className="w-3.5 h-3.5" /> Abbonamenti
        </p>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Annulla' : 'Attiva piano'}
        </Button>
      </div>

      {showForm && (
        <div className="grid grid-cols-2 gap-2 p-2 rounded-lg bg-muted/40">
          <div className="col-span-2">
            <Label className="text-xs">Listino</Label>
            <select
              className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              value={preset}
              onChange={(e) => {
                const key = e.target.value
                setPreset(key)
                const p = PRESETS.find((x) => x.key === key)
                if (p) { setAmount(p.amount); setQuota(p.quota) }
              }}
            >
              {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Piano</Label>
            <select
              className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              value={tier}
              onChange={(e) => setTier(e.target.value as SubscriptionTier)}
            >
              <option value="SCHOOL">Scuola</option>
              <option value="NETWORK">Rete</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Rif. ordine</Label>
            <Input className="h-9" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} placeholder="es. ODA-2026-..." />
          </div>
          <div>
            <Label className="text-xs">Valido dal</Label>
            <Input type="date" className="h-9" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Valido fino al</Label>
            <Input type="date" className="h-9" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Importo €</Label>
            <Input type="number" step="0.01" className="h-9" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="opzionale" />
          </div>
          <div>
            <Label className="text-xs">Valutazioni incluse/anno</Label>
            <Input type="number" className="h-9" value={quota} onChange={(e) => setQuota(e.target.value)} placeholder="vuoto = illimitata" />
          </div>
          <div className="flex items-end col-span-2">
            <Button size="sm" className="h-9 w-full" disabled={creating} onClick={() => create()}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma attivazione'}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-10 rounded-lg" />
      ) : subs && subs.length > 0 ? (
        <div className="space-y-1.5">
          {subs.map((s: AdminSubscription) => (
            <div key={s.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <PlanBadge plan={s.tier} />
                <span className="text-xs text-muted-foreground">
                  {s.valid_from.slice(0, 10)} → {s.valid_until.slice(0, 10)}
                </span>
                <span className="text-xs text-muted-foreground">
                  · {s.quota != null ? `${s.quota} valutazioni/anno` : 'illimitata'}
                </span>
                {s.is_active ? (
                  <Badge variant="outline" className="text-xs text-verde border-verde/40">Attivo</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    {SUB_STATUS_LABEL[s.status]}
                  </Badge>
                )}
              </div>
              {(s.status === 'ACTIVE') && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    title="Cambia la quota inclusa (upgrade/downgrade)"
                    onClick={() => editQuota(s)}
                  >
                    Modifica quota
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-destructive"
                    title="Sospendi (riporta l'istituto a Free)"
                    onClick={() => {
                      if (confirm('Sospendere questo abbonamento? L\'istituto torna al piano Free.')) {
                        changeStatus({ id: s.id, status: 'SUSPENDED' })
                      }
                    }}
                  >
                    <Ban className="w-3.5 h-3.5 mr-1" /> Sospendi
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground py-1">Nessun abbonamento. Piano corrente: Free.</p>
      )}
    </div>
  )
}

/** Admin-only tenant provisioning (there is no dirigente self-service). */
function NewTenantForm() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [codice, setCodice] = useState('')
  const [city, setCity] = useState('')
  const [province, setProvince] = useState('')
  const [domain, setDomain] = useState('')
  const [dirigenteEmail, setDirigenteEmail] = useState('')

  const { mutate: create, isPending } = useMutation({
    mutationFn: () =>
      adminApi.createTenant({
        name: name.trim(),
        codice_meccanografico: codice.trim() || undefined,
        city: city.trim() || undefined,
        province: province.trim() || undefined,
        email_domain: domain.trim() || undefined,
        dirigente_email: dirigenteEmail.trim() || undefined,
      }),
    onSuccess: (d: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      queryClient.invalidateQueries({ queryKey: ['admin-overview'] })
      setName(''); setCodice(''); setCity(''); setProvince(''); setDomain(''); setDirigenteEmail('')
      setOpen(false)
      toast({
        title: d?.backfilled_members
          ? `Istituto creato · ${d.backfilled_members} docenti associati`
          : 'Istituto creato',
      })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <PlusCircle className="w-4 h-4 mr-1.5" /> Nuovo istituto
      </Button>
    )
  }

  return (
    <Card>
      <CardContent className="pt-4 pb-3 grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Label className="text-xs">Nome istituto *</Label>
          <Input className="h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. I.C. Giovanni Verga" />
        </div>
        <div>
          <Label className="text-xs">Codice meccanografico</Label>
          <Input className="h-9" value={codice} onChange={(e) => setCodice(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Dominio istituzionale</Label>
          <Input className="h-9" value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())} placeholder="es. icverga.edu.it" />
        </div>
        <div>
          <Label className="text-xs">Città</Label>
          <Input className="h-9" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Provincia</Label>
          <Input className="h-9" value={province} onChange={(e) => setProvince(e.target.value.toUpperCase())} maxLength={5} />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Email Dirigente (facoltativa)</Label>
          <Input className="h-9" type="email" value={dirigenteEmail} onChange={(e) => setDirigenteEmail(e.target.value)} placeholder="dirigente@icverga.edu.it" />
        </div>
        <div className="col-span-2 flex gap-2 justify-end mt-1">
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Annulla</Button>
          <Button size="sm" disabled={isPending || !name.trim()} onClick={() => create()}>
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea istituto'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/** Admin-only tenant configuration: institutional domain (the lever that
 * grants the active plan to all teachers) and DIRIGENTE designation by email. */
function TenantSettings({ tenant }: { tenant: AdminTenant }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [domain, setDomain] = useState(tenant.email_domain || '')
  const [dirigenteEmail, setDirigenteEmail] = useState('')

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    queryClient.invalidateQueries({ queryKey: ['admin-tenant-members'] })
  }

  const { mutate: saveDomain, isPending: savingDomain } = useMutation({
    mutationFn: () => adminApi.updateTenant(tenant.id, { email_domain: domain.trim() }),
    onSuccess: (d: any) => {
      refresh()
      toast({
        title: d?.backfilled_members
          ? `Dominio aggiornato · ${d.backfilled_members} docenti associati`
          : 'Dominio aggiornato',
      })
    },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: assignDirigente, isPending: assigning } = useMutation({
    mutationFn: () => adminApi.assignDirigenteByEmail(tenant.id, dirigenteEmail.trim()),
    onSuccess: (d: any) => { refresh(); setDirigenteEmail(''); toast({ title: d.message }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  // P8: verifica identità Dirigente via IPA + PEC
  const { mutate: ipaLookup, isPending: ipaLoading } = useMutation({
    mutationFn: () => adminApi.ipaLookup(tenant.id),
    onSuccess: (d: any) => {
      refresh()
      toast({
        title: d?.pec
          ? `IPA: ${d.denominazione} · PEC ${d.pec}`
          : `IPA: ${d?.denominazione ?? 'ente trovato'} (nessuna PEC nel record)`,
        description: d?.responsabile
          ? `Responsabile IPA: ${d.responsabile} (${d.titolo_responsabile ?? ''})`
          : undefined,
      })
    },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Lookup IPA non riuscito', variant: 'destructive' }),
  })
  const { mutate: sendPec, isPending: pecSending } = useMutation({
    mutationFn: () => adminApi.sendPecVerification(tenant.id),
    onSuccess: (d: any) => toast({ title: d.message }),
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Invio PEC non riuscito', variant: 'destructive' }),
  })

  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
        <Globe className="w-3.5 h-3.5" /> Configurazione istituto
      </p>
      {!tenant.email_domain && (
        <Callout variant="warning" title="Dominio non configurato" icon={<AlertTriangle className="w-3.5 h-3.5" />}>
          Senza dominio istituzionale i docenti non vengono associati automaticamente
          al login e non ricevono il piano attivo. Impostalo qui sotto.
        </Callout>
      )}
      <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">Dominio istituzionale (abilita i docenti)</Label>
          <Input className="h-9" value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())} placeholder="es. icverga.edu.it" />
        </div>
        <Button size="sm" className="h-9" disabled={savingDomain} onClick={() => saveDomain()}>
          {savingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">Designa Dirigente via email</Label>
          <Input className="h-9" type="email" value={dirigenteEmail} onChange={(e) => setDirigenteEmail(e.target.value)} placeholder="dirigente@icverga.edu.it" />
        </div>
        <Button size="sm" variant="outline" className="h-9" disabled={assigning || !dirigenteEmail.trim()} onClick={() => assignDirigente()}>
          {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <p className="text-xs text-muted-foreground flex-1">
          Verifica identità Dirigente: lookup dei dati ufficiali su IPA (per codice
          meccanografico), poi invio del codice alla PEC istituzionale.
        </p>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={ipaLoading}
                onClick={() => ipaLookup()}>
          {ipaLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Lookup IPA'}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={pecSending}
                onClick={() => sendPec()}>
          {pecSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invia codice PEC'}
        </Button>
      </div>
    </div>
  )
}

function StatBox({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="p-3 rounded-lg border border-border text-center">
      <p className={`text-2xl font-bold tabular-nums ${accent || ''}`}>{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  )
}

export default function AdminPanel() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [tenantQuery, setTenantQuery] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null)
  const [bgAssessmentId, setBgAssessmentId] = useState('')
  const [bgReason, setBgReason] = useState('')

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: adminApi.overview,
  })

  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['admin-tenants', tenantQuery],
    queryFn: () => adminApi.listTenants(tenantQuery ? { q: tenantQuery } : undefined),
  })

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users', userQuery],
    queryFn: () => adminApi.listUsers(userQuery ? { q: userQuery } : undefined),
  })

  const { data: tenantMembers } = useQuery({
    queryKey: ['admin-tenant-members', expandedTenant],
    queryFn: () => adminApi.listTenantMembers(expandedTenant!),
    enabled: !!expandedTenant,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-overview'] })
    queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    queryClient.invalidateQueries({ queryKey: ['admin-tenant-members'] })
  }

  const { mutate: setStatus } = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      adminApi.setUserStatus(id, active),
    onSuccess: () => { invalidate(); toast({ title: 'Stato account aggiornato' }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: erase } = useMutation({
    mutationFn: (id: string) => adminApi.gdprErase(id),
    onSuccess: () => { invalidate(); toast({ title: 'Dati personali anonimizzati' }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: promote } = useMutation({
    mutationFn: ({ schoolId, userId }: { schoolId: string; userId: string }) =>
      adminApi.reassignDirigente(schoolId, userId),
    onSuccess: (d: any) => { invalidate(); toast({ title: d.message }) },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: breakGlass, isPending: bgPending } = useMutation({
    mutationFn: () => adminApi.breakGlass(bgAssessmentId.trim(), bgReason),
    onSuccess: (assessment) => {
      toast({ title: 'Accesso registrato nell\'audit log' })
      window.open(`/assessment/${assessment.id}`, '_blank')
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 stagger">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ServerCog className="w-6 h-6 text-mim-blue" />
          Amministrazione Piattaforma
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gestione tenant e account · metriche aggregate · nessun accesso ordinario ai contenuti
        </p>
      </div>

      {/* Overview */}
      {overviewLoading ? (
        <Skeleton className="h-28 rounded-xl" />
      ) : overview && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          <StatBox label="Istituti" value={overview.schools} />
          <StatBox label="Utenti" value={overview.users} />
          <StatBox label="Attivi" value={overview.active_users} />
          <StatBox label="Valutazioni" value={overview.assessments.total} />
          <StatBox label="Verde" value={overview.assessments.verde} accent="text-verde" />
          <StatBox label="Giallo" value={overview.assessments.giallo} accent="text-giallo" />
          <StatBox label="Rosso" value={overview.assessments.rosso} accent="text-rosso" />
          <StatBox label="Fallite" value={overview.assessments.failed} accent="text-muted-foreground" />
        </div>
      )}

      <Tabs defaultValue="tenants">
        <TabsList>
          <TabsTrigger value="tenants">
            <Building2 className="w-4 h-4 mr-1.5" /> Istituti
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-1.5" /> Account
          </TabsTrigger>
          <TabsTrigger value="breakglass">
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Break-glass
          </TabsTrigger>
        </TabsList>

        {/* Tenants */}
        <TabsContent value="tenants" className="space-y-3 mt-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca istituto…"
              value={tenantQuery}
              onChange={(e) => setTenantQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <NewTenantForm />
          {tenantsLoading ? (
            <Skeleton className="h-40 rounded-xl" />
          ) : (
            <div className="space-y-2">
              {tenants?.map((s: AdminTenant) => (
                <Card key={s.id}>
                  <CardContent className="pt-4 pb-3">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between text-left"
                      onClick={() => setExpandedTenant(expandedTenant === s.id ? null : s.id)}
                    >
                      <div>
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[s.codice_meccanografico, s.city, s.email_domain].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
                        {!s.email_domain && (
                          <Badge
                            variant="outline"
                            className={`text-xs gap-1 ${s.plan !== 'FREE'
                              ? 'text-rosso border-rosso/40'
                              : 'text-giallo-dark border-giallo/40'}`}
                            title="Senza dominio istituzionale i docenti non vengono associati automaticamente al login"
                          >
                            <AlertTriangle className="w-3 h-3" /> Dominio mancante
                          </Badge>
                        )}
                        <PlanBadge plan={s.plan} />
                        <span>{s.members} membri</span>
                        <span>{s.assessments} valutazioni</span>
                      </div>
                    </button>
                    {expandedTenant === s.id && (
                      <TenantSettings tenant={s} />
                    )}
                    {expandedTenant === s.id && (
                      <TenantSubscriptions schoolId={s.id} />
                    )}
                    {expandedTenant === s.id && tenantMembers && (
                      <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                        {tenantMembers.map((m: any) => (
                          <div key={m.id} className="flex items-center justify-between text-sm">
                            <div className="min-w-0">
                              <span className="font-medium">{m.name}</span>{' '}
                              <span className="text-xs text-muted-foreground">{m.email}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="text-xs">{roleLabel(m.role)}</Badge>
                              {m.role !== 'DIRIGENTE' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs"
                                  title="Promuovi a Dirigente (recupero istituto orfano)"
                                  onClick={() => {
                                    if (confirm(`Promuovere ${m.name} a Dirigente?`)) {
                                      promote({ schoolId: s.id, userId: m.id })
                                    }
                                  }}
                                >
                                  <Crown className="w-3.5 h-3.5 mr-1" /> Dirigente
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {tenants?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Nessun istituto trovato</p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="space-y-3 mt-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome o email…"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {usersLoading ? (
            <Skeleton className="h-40 rounded-xl" />
          ) : (
            <div className="space-y-2">
              {users?.map((u: AdminUser) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {u.name}
                      {!u.is_active && (
                        <Badge variant="outline" className="ml-2 text-xs text-destructive border-destructive/40">
                          disattivato
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {u.email} · {roleLabel(u.role)}{u.school ? ` · ${u.school}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      title={u.is_active ? 'Disattiva account' : 'Riattiva account'}
                      onClick={() => setStatus({ id: u.id, active: !u.is_active })}
                    >
                      {u.is_active
                        ? <UserX className="w-4 h-4 text-destructive" />
                        : <UserCheck className="w-4 h-4 text-verde" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Cancellazione GDPR (anonimizza dati personali)"
                      onClick={() => {
                        if (confirm(
                          `Anonimizzare definitivamente i dati personali di ${u.name}? ` +
                          'L\'operazione è irreversibile.'
                        )) {
                          erase(u.id)
                        }
                      }}
                    >
                      <Eraser className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
              {users?.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Nessun utente trovato</p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Break-glass */}
        <TabsContent value="breakglass" className="mt-4">
          <Card className="border-giallo/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-giallo-dark" />
                Accesso eccezionale ai contenuti
              </CardTitle>
              <CardDescription className="text-xs">
                Da usare solo su richiesta esplicita dell'istituto (supporto, incident).
                Ogni accesso viene registrato nell'audit log con la motivazione,
                prima che il contenuto sia restituito.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="bg-id">ID valutazione</Label>
                <Input
                  id="bg-id"
                  placeholder="UUID della valutazione"
                  value={bgAssessmentId}
                  onChange={(e) => setBgAssessmentId(e.target.value)}
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label htmlFor="bg-reason">Motivazione (min. 20 caratteri) *</Label>
                <Textarea
                  id="bg-reason"
                  placeholder="Es: ticket #42 del 11/06/2026 — il DPO dell'istituto segnala verdetto incoerente e chiede verifica…"
                  value={bgReason}
                  onChange={(e) => setBgReason(e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={() => breakGlass()}
                disabled={bgPending || bgAssessmentId.trim().length < 10 || bgReason.trim().length < 20}
                className="bg-giallo hover:bg-giallo-dark text-foreground"
              >
                {bgPending
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <FileSearch className="w-4 h-4 mr-2" />}
                Accedi e registra
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
